import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  X, CloudArrowUp, FileText, CheckCircle, ArrowRight, Plus, XCircle,
  Thermometer, MapPin, Clock, ChartLineUp, Warning, Drop, Sun, MapTrifold,
  Spinner,
} from "@phosphor-icons/react";
import L from "leaflet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LoggerImportDialog — intelligent USB/GPS logger upload workflow.
 *
 * Supports:
 *   - Single file → existing flow (upload → match → attached).
 *   - Multi-file drop / batch upload → batch queue UX where each staged
 *     import (one per file × per route_id group) is reviewed independently.
 *   - Auto-split by route_id / logger_id is server-side; the queue surfaces
 *     each split group as its own queue item so operators see RTE-USB-001
 *     and RTE-USB-002 as two distinct shipments-to-match.
 *
 * Auto-attach: if tenant threshold ≤ top candidate score AND the toggle is on,
 * the backend auto-confirms during upload — those queue items land as "attached".
 */
export default function LoggerImportDialog({ open, onClose, onAttached }) {
  const [state, setState] = useState("upload");      // upload | match | attached | create | queue
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [importDoc, setImportDoc] = useState(null);   // currently-focused import
  const [queue, setQueue] = useState([]);             // [{...import}, ...] for batch
  const [createForm, setCreateForm] = useState({ name: "", customer: "", cargo_type: "Pharma", temp_min: 32, temp_max: 40 });
  const fileInputRef = useRef(null);

  if (!open) return null;

  const reset = () => {
    setState("upload"); setImportDoc(null); setQueue([]);
    setCreateForm({ name: "", customer: "", cargo_type: "Pharma", temp_min: 32, temp_max: 40 });
  };
  const closeAll = () => { reset(); onClose?.(); };

  // Normalise any /upload or /batch-upload response into a flat list of imports.
  const flattenImports = (data) => {
    if (data?.batch) {
      if (Array.isArray(data.imports)) return data.imports;
      if (Array.isArray(data.files)) {
        return data.files.flatMap((f) => (f.imports || []).map((i) => ({ ...i, _filename: f.filename })));
      }
    }
    return [data];
  };

  const upload = async (files) => {
    const fileArr = Array.isArray(files) ? files : (files instanceof FileList ? Array.from(files) : [files]);
    const cleanFiles = fileArr.filter(Boolean);
    if (cleanFiles.length === 0) return;
    setUploading(true);
    try {
      let imports = [];
      if (cleanFiles.length === 1) {
        const fd = new FormData(); fd.append("file", cleanFiles[0]);
        const res = await axios.post(`${API}/logger-imports/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        imports = flattenImports(res.data);
      } else {
        const fd = new FormData();
        cleanFiles.forEach((f) => fd.append("files", f));
        const res = await axios.post(`${API}/logger-imports/batch-upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        imports = flattenImports(res.data);
      }

      // Single import → existing flow (back-compat).
      if (imports.length === 1) {
        const only = imports[0];
        setImportDoc(only);
        if (only.status === "attached") {
          setState("attached");
          toast.success("Logger auto-attached — score above tenant threshold");
          onAttached?.(only.replay_summary);
        } else {
          setState("match");
          toast.success(`Parsed ${only.stats.rows} rows · ${only.candidates?.length || 0} candidates`);
        }
        return;
      }

      // Multi-import (batch) → queue view.
      setQueue(imports);
      setState("queue");
      const total = imports.reduce((a, i) => a + (i.stats?.rows || 0), 0);
      const auto = imports.filter((i) => i.status === "attached").length;
      toast.success(`Staged ${imports.length} imports · ${total} rows total` + (auto ? ` · ${auto} auto-attached` : ""));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Refresh a queue item in place after attach/create (so its status & summary update).
  const updateQueueItem = (id, patch) => {
    setQueue((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  };

  const confirm = async (shipment_id) => {
    if (!importDoc) return;
    setConfirming(true);
    try {
      const res = await axios.post(
        `${API}/logger-imports/${importDoc.id}/confirm`,
        { shipment_id },
      );
      const patch = { replay_summary: res.data.summary, status: "attached" };
      setImportDoc((prev) => ({ ...prev, ...patch }));
      updateQueueItem(importDoc.id, patch);
      toast.success(`Replayed ${res.data.summary.points_replayed} pts · ${res.data.summary.alerts_generated} alerts`);
      onAttached?.(res.data.summary);
      // If we're in queue mode, return to queue view; otherwise jump to attached.
      setState(queue.length > 1 ? "queue" : "attached");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Attach failed");
    } finally {
      setConfirming(false);
    }
  };

  const createAndAttach = async () => {
    if (!createForm.name.trim()) { toast.error("Shipment name required"); return; }
    if (!importDoc) return;
    setConfirming(true);
    try {
      const res = await axios.post(
        `${API}/logger-imports/${importDoc.id}/create-shipment`,
        { ...createForm, temp_min: parseFloat(createForm.temp_min), temp_max: parseFloat(createForm.temp_max) },
      );
      const patch = { replay_summary: res.data.summary, status: "attached" };
      setImportDoc((prev) => ({ ...prev, ...patch }));
      updateQueueItem(importDoc.id, patch);
      toast.success(`Shipment "${createForm.name}" created and logger attached`);
      onAttached?.(res.data.summary);
      setState(queue.length > 1 ? "queue" : "attached");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Create failed");
    } finally {
      setConfirming(false);
    }
  };

  // From queue: open a specific import in the existing match/create flow.
  const openQueueItem = (item) => {
    setImportDoc(item);
    if (item.status === "attached") setState("attached");
    else setState("match");
  };
  const backToQueue = () => { setImportDoc(null); setState("queue"); };

  return (
    <div className="fixed inset-0 z-[1900] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6"
      onClick={closeAll}>
      <Card className="bg-zinc-950 border-zinc-800 w-full max-w-4xl flex flex-col"
        style={{ maxHeight: "92vh" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="logger-import-dialog">

        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CloudArrowUp size={18} weight="duotone" className="text-teal-400" />
            <div>
              <div className="text-zinc-100 font-semibold">Import Logger File</div>
              <div className="text-xs text-zinc-500">
                {state === "upload"   && "Drop one or many CSVs — multi-route files auto-split per logger."}
                {state === "queue"    && `Batch queue · ${queue.length} staged imports.`}
                {state === "match"    && "Pick the best-matching shipment, or create a new one."}
                {state === "create"   && "New shipment from logger data."}
                {state === "attached" && "Logger attached. Data replayed through alert engine."}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(state === "match" || state === "create" || state === "attached") && queue.length > 1 && (
              <Button size="sm" variant="outline"
                onClick={backToQueue}
                className="border-zinc-700 hover:bg-zinc-800 text-xs h-7"
                data-testid="logger-back-to-queue">
                ← Queue
              </Button>
            )}
            <button onClick={closeAll} className="text-zinc-500 hover:text-zinc-200"
              data-testid="logger-import-close"><X size={18} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {state === "upload" && (
            <UploadDropzone uploading={uploading} onPick={upload} ref={fileInputRef} />
          )}
          {state === "queue" && (
            <BatchQueue items={queue} onSelect={openQueueItem} onClose={closeAll}
              onBulkAttached={(result) => {
                // Mark every attached import attached in the queue
                if (result?.results?.length) {
                  setQueue((prev) => prev.map((q) => {
                    const hit = result.results.find((r) => r.import_id === q.id);
                    return hit ? { ...q, status: "attached", replay_summary: hit.summary } : q;
                  }));
                  onAttached?.(result.results[0].summary);
                }
              }} />
          )}
          {state === "match" && importDoc && (
            <MatchStep importDoc={importDoc} onConfirm={confirm}
              onCreate={() => setState("create")} confirming={confirming} />
          )}
          {state === "create" && importDoc && (
            <CreateStep form={createForm} setForm={setCreateForm}
              importDoc={importDoc} onBack={() => setState("match")}
              onSubmit={createAndAttach} submitting={confirming} />
          )}
          {state === "attached" && importDoc && (
            <AttachedStep summary={importDoc.replay_summary} stats={importDoc.stats}
              onDone={queue.length > 1 ? backToQueue : closeAll}
              doneLabel={queue.length > 1 ? "Back to queue" : "Done"} />
          )}
        </div>
      </Card>
    </div>
  );
}

import { forwardRef } from "react";
const UploadDropzone = forwardRef(function UploadDropzone({ uploading, onPick }, ref) {
  const onDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) onPick(Array.from(files));
  };
  return (
    <div onDragOver={(e) => e.preventDefault()} onDrop={onDrop}
      className="border-2 border-dashed border-zinc-800 hover:border-teal-500/50 rounded-md p-12 text-center transition-colors cursor-pointer"
      onClick={() => ref?.current?.click()}
      data-testid="logger-dropzone">
      <CloudArrowUp size={42} weight="duotone" className="mx-auto text-zinc-600 mb-3" />
      <div className="text-zinc-200 font-medium mb-1">
        {uploading ? "Parsing…" : "Drop one or many logger CSVs here, or click to browse"}
      </div>
      <div className="text-[10px] text-zinc-500 font-mono mb-4">
        Required: <b>timestamp</b> · Optional: lat, lng, temperature, humidity, light, battery, route_id, logger_id<br/>
        Multi-route files auto-split per route_id / logger_id.
      </div>
      <Input ref={ref} type="file" accept=".csv" multiple className="hidden"
        onChange={(e) => onPick(Array.from(e.target.files || []))} data-testid="logger-file-input" />
      <Button size="sm" disabled={uploading}
        className="bg-teal-500 hover:bg-teal-400 text-zinc-950"
        data-testid="logger-browse-btn">
        {uploading ? "Parsing…" : "Browse files"}
      </Button>
    </div>
  );
});

/**
 * BatchQueue — list of staged imports from a multi-file or multi-route upload.
 * Each row: filename / route / logger id, parse stats, top candidate score,
 * status (pending / attached). Click a row to drill into match flow.
 *
 * Bulk-attach: when ≥2 imports are pending AND they all share a batch_id,
 * the operator can attach the whole batch to a single shipment in one click.
 * Each import is still replayed independently so the shipment ends up with
 * multiple device rows (e.g. redundant USB loggers on one pallet group).
 */
function BatchQueue({ items, onSelect, onClose, onBulkAttached }) {
  const pending  = items.filter((i) => i.status !== "attached");
  const attached = items.filter((i) => i.status === "attached");
  const batchId  = items[0]?.batch_id;
  const sameBatch = batchId && items.every((i) => i.batch_id === batchId);

  const [showBulkPicker, setShowBulkPicker] = useState(false);

  return (
    <div className="space-y-4" data-testid="logger-batch-queue">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-mono">
          {pending.length} pending · {attached.length} attached
        </div>
        <div className="flex items-center gap-2">
          {pending.length >= 2 && sameBatch && !showBulkPicker && (
            <Button size="sm" variant="outline"
              onClick={() => setShowBulkPicker(true)}
              className="border-violet-700/50 text-violet-300 hover:bg-violet-500/10 text-xs h-8"
              data-testid="logger-bulk-attach-open">
              <ChartLineUp size={12} className="mr-1" /> Attach all to this shipment
            </Button>
          )}
          {attached.length === items.length && (
            <Button size="sm" onClick={onClose}
              className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-xs h-8"
              data-testid="logger-batch-done">
              <CheckCircle size={12} className="mr-1" /> All done — close
            </Button>
          )}
        </div>
      </div>

      {showBulkPicker && (
        <BulkAttachPicker
          batchId={batchId}
          pendingCount={pending.length}
          onCancel={() => setShowBulkPicker(false)}
          onDone={(result) => {
            setShowBulkPicker(false);
            onBulkAttached?.(result);
          }}
        />
      )}

      <div className="space-y-2">
        {items.map((item) => (
          <BatchRow key={item.id} item={item} onClick={() => onSelect(item)} />
        ))}
      </div>
    </div>
  );
}

/**
 * BulkAttachPicker — inline shipment search + confirm-with-warning UX for the
 * batch-level "Attach all to this shipment" flow. Operator picks ONE shipment;
 * every pending import in the batch is replayed against it.
 */
function BulkAttachPicker({ batchId, pendingCount, onCancel, onDone }) {
  const [q, setQ] = useState("");
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    let active = true;
    const t = setTimeout(() => {
      axios.get(`${API}/shipments/?q=${encodeURIComponent(q)}&limit=8`)
        .then((r) => {
          if (active) {
            const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
            setMatches(items.slice(0, 8));
          }
        })
        .catch(() => {});
    }, 200);
    return () => { active = false; clearTimeout(t); };
  }, [q]);

  const submit = async () => {
    if (!selected) { toast.error("Pick a shipment"); return; }
    setConfirming(true);
    try {
      const r = await axios.post(
        `${API}/logger-imports/batch/${batchId}/bulk-attach`,
        { shipment_id: selected.id },
      );
      toast.success(`Attached ${r.data.attached} loggers to "${r.data.shipment_name}"`
                    + (r.data.errors ? ` · ${r.data.errors} failed` : ""));
      onDone?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Bulk attach failed");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Card className="bg-violet-500/5 border-violet-500/30 p-3 space-y-3"
      data-testid="logger-bulk-attach-picker">
      <div className="flex items-center gap-2">
        <Warning size={14} weight="duotone" className="text-amber-400" />
        <span className="text-xs text-zinc-200">
          Attach <b>{pendingCount} pending logger{pendingCount === 1 ? "" : "s"}</b> to one shipment.
        </span>
      </div>
      <p className="text-[10px] text-zinc-500 leading-relaxed">
        Each logger is replayed independently — the shipment will show multiple attached loggers,
        each with its own sensor stream and audit rows. The logger data is not merged.
      </p>
      <Input value={q} onChange={(e) => { setQ(e.target.value); setSelected(null); }}
        placeholder="Search shipment name or customer…"
        className="bg-zinc-950 border-zinc-800 text-xs h-9"
        data-testid="logger-bulk-attach-search" />
      {matches.length > 0 && !selected && (
        <div className="max-h-44 overflow-y-auto border border-zinc-800 rounded-sm divide-y divide-zinc-900">
          {matches.map((s) => (
            <button key={s.id} onClick={() => setSelected(s)}
              className="w-full text-left px-3 py-2 hover:bg-zinc-800/60 text-xs text-zinc-200"
              data-testid={`logger-bulk-pick-${s.id}`}>
              <div className="font-medium truncate">{s.name || s.id}</div>
              <div className="text-[10px] text-zinc-500 font-mono">
                {s.customer || "—"} · {(s.status || "").toUpperCase()} · {s.id.slice(0, 8)}
              </div>
            </button>
          ))}
        </div>
      )}
      {selected && !confirmed && (
        <Card className="bg-amber-500/5 border-amber-500/30 p-3 space-y-2">
          <div className="text-xs text-zinc-200">
            Attach to <b>{selected.name}</b>?
            <div className="text-[10px] text-zinc-500 font-mono mt-0.5">
              {selected.customer || "—"} · {selected.id.slice(0, 8)}
            </div>
          </div>
          <div className="text-[10px] text-amber-300 leading-relaxed">
            This will replay {pendingCount} logger trace{pendingCount === 1 ? "" : "s"} into this shipment.
            Each replay fires its own alerts and audit rows. This action cannot be undone.
          </div>
          <label className="flex items-center gap-2 text-[10px] text-zinc-300 cursor-pointer">
            <input type="checkbox" onChange={(e) => setConfirmed(e.target.checked)}
              className="rounded-sm" data-testid="logger-bulk-confirm-checkbox" />
            I understand — proceed
          </label>
        </Card>
      )}
      <div className="flex items-center justify-end gap-2 pt-1">
        <Button size="sm" variant="outline"
          onClick={onCancel} disabled={confirming}
          className="border-zinc-700 hover:bg-zinc-800 text-xs h-8"
          data-testid="logger-bulk-cancel">
          Cancel
        </Button>
        <Button size="sm" onClick={submit}
          disabled={!selected || !confirmed || confirming}
          className="bg-violet-500 hover:bg-violet-400 text-zinc-50 text-xs h-8"
          data-testid="logger-bulk-attach-submit">
          {confirming ? "Attaching…" : `Attach all ${pendingCount}`}
        </Button>
      </div>
    </Card>
  );
}

function BatchRow({ item, onClick }) {
  const s = item.stats || {};
  const top = item.candidates?.[0];
  const isAttached = item.status === "attached";
  const sum = item.replay_summary;

  return (
    <Card onClick={onClick}
      className={`p-3 cursor-pointer transition-colors border ${isAttached
        ? "bg-emerald-500/5 border-emerald-500/30 hover:border-emerald-500/50"
        : "bg-zinc-900 border-zinc-800 hover:border-teal-500/40"}`}
      data-testid={`batch-row-${item.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <FileText size={12} className="text-zinc-400 flex-shrink-0" />
            <span className="text-xs font-medium text-zinc-200 truncate">
              {item._filename || item.filename}
            </span>
            {item.route_id && (
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-teal-500/10 text-teal-400">
                {item.route_id}
              </span>
            )}
            {item.logger_id && (
              <span className="text-[10px] font-mono text-zinc-500">{item.logger_id}</span>
            )}
            {isAttached ? (
              <span className="ml-auto text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-300">
                attached
              </span>
            ) : (
              <span className="ml-auto text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm bg-amber-500/15 text-amber-300">
                pending
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-2 flex-wrap text-[10px] font-mono text-zinc-500">
            <span>{s.rows} rows · {s.gps_points} gps</span>
            <span><Thermometer size={9} className="inline" /> {s.temp_min}–{s.temp_max}°F</span>
            {s.first_ts && s.last_ts && (
              <span>{new Date(s.first_ts).toLocaleString([], {month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})} →
                {" " + new Date(s.last_ts).toLocaleString([], {hour:"2-digit",minute:"2-digit"})}</span>
            )}
            {!isAttached && top && (
              <span className="text-teal-300">top match: {top.shipment_name} ({top.score})</span>
            )}
            {isAttached && sum && (
              <span className="text-emerald-300">
                {sum.points_replayed} replayed · {sum.alerts_generated} alerts
              </span>
            )}
          </div>
        </div>
        <ArrowRight size={14} className="text-zinc-600 flex-shrink-0 mt-1" />
      </div>
    </Card>
  );
}

function MatchStep({ importDoc, onConfirm, onCreate, confirming }) {
  const s = importDoc.stats || {};
  const samplePoints = importDoc.sample_points || [];

  // Sensor summary derived from sample points (humidity / light events).
  const humidities = samplePoints.map((p) => p.humidity).filter((v) => v != null);
  const humAvg = humidities.length ? Math.round(humidities.reduce((a, b) => a + b, 0) / humidities.length) : null;
  const humMin = humidities.length ? Math.min(...humidities) : null;
  const humMax = humidities.length ? Math.max(...humidities) : null;
  // Light events = samples where light > 0 (numeric lux) — i.e. potential container open
  const lightEvents = samplePoints.filter((p) => typeof p.light === "number" && p.light > 0).length;

  // Lazy reverse-geocode (only once per import).
  const [geo, setGeo] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  useEffect(() => {
    let active = true;
    if (!importDoc?.id) return;
    setGeoLoading(true);
    axios.get(`${API}/logger-imports/${importDoc.id}/geocode`)
      .then((r) => { if (active) setGeo(r.data); })
      .catch(() => {})
      .finally(() => { if (active) setGeoLoading(false); });
    return () => { active = false; };
  }, [importDoc?.id]);

  return (
    <div className="space-y-4">
      {/* Parsed stats summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="logger-stats">
        <StatCard icon={<FileText size={14} className="text-teal-400" />} label="Rows" value={s.rows} sub={`${s.bad_rows || 0} skipped`} />
        <StatCard icon={<MapPin size={14} className="text-sky-400" />} label="GPS Points" value={s.gps_points || 0} sub={s.first_point ? `${s.first_point.lat.toFixed(3)} → ${s.last_point.lat.toFixed(3)}` : "no GPS"} />
        <StatCard icon={<Clock size={14} className="text-amber-400" />} label="Time Range"
          value={s.first_ts ? new Date(s.first_ts).toLocaleString([], {month:"short", day:"numeric"}) : "—"}
          sub={s.last_ts ? new Date(s.last_ts).toLocaleString([], {month:"short", day:"numeric"}) : ""} />
        <StatCard icon={<Thermometer size={14} className="text-red-400" />} label="Temp (°F)"
          value={s.temp_min != null ? `${s.temp_min} – ${s.temp_max}` : "—"} />
        <StatCard icon={<Drop size={14} className="text-sky-400" />} label="Humidity (%)"
          value={humAvg != null ? `${humAvg} avg` : "—"}
          sub={humMin != null ? `${humMin} – ${humMax}` : ""} />
        <StatCard icon={<Sun size={14} className="text-amber-400" />} label="Light Events"
          value={lightEvents}
          sub={lightEvents > 0 ? "possible container opens" : "no exposure"} />
        <StatCard icon={<MapPin size={14} className="text-emerald-400" />} label="Origin (geo)"
          value={geo?.origin?.name || (geoLoading ? "…" : "—")}
          sub={geo?.origin?.city ? `${geo.origin.city}${geo.origin.region ? ", " + geo.origin.region : ""}` : ""} />
        <StatCard icon={<MapPin size={14} className="text-emerald-400" />} label="Destination (geo)"
          value={geo?.destination?.name || (geoLoading ? "…" : "—")}
          sub={geo?.destination?.city ? `${geo.destination.city}${geo.destination.region ? ", " + geo.destination.region : ""}` : ""} />
      </div>

      {/* Route preview map */}
      <RoutePreviewMap points={samplePoints} first={s.first_point} last={s.last_point} />

      {/* Candidates */}
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2 block">
          Matched Shipments ({importDoc.candidates?.length || 0})
        </Label>

        {/* Low-confidence callout — shown when no candidate clears 40 score.
            Prevents operators from scrolling through 6 dead-end matches before
            finding the create CTA at the bottom. */}
        {Array.isArray(importDoc.candidates) && importDoc.candidates.length > 0 &&
         (importDoc.candidates[0]?.score ?? 0) < 40 && (
          <Card
            className="p-3 mb-2 bg-teal-500/5 border-teal-500/30 flex items-center justify-between gap-3"
            data-testid="logger-low-confidence-banner"
          >
            <div className="flex items-start gap-2 min-w-0">
              <Warning size={16} weight="duotone" className="text-teal-300 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-zinc-200 font-medium">No strong shipment match</p>
                <p className="text-[10px] text-zinc-500 mt-0.5">
                  Top match scored {Math.round((importDoc.candidates[0]?.score ?? 0) * 10) / 10}/100.
                  Recommended: create a new shipment from this logger trace.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={onCreate}
              className="bg-teal-500 hover:bg-teal-400 text-zinc-950 text-xs h-8 shrink-0"
              data-testid="logger-low-confidence-create-btn"
            >
              <Plus size={11} className="mr-1" /> Create New Shipment
            </Button>
          </Card>
        )}

        {(!importDoc.candidates || importDoc.candidates.length === 0) ? (
          <Card className="p-4 bg-zinc-900 border-amber-500/30 text-center">
            <Warning size={20} weight="duotone" className="mx-auto text-amber-400 mb-2" />
            <p className="text-sm text-zinc-300">No candidate shipments found.</p>
            <p className="text-[10px] text-zinc-500 mt-1">Create a new shipment from this logger trace below.</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {importDoc.candidates.map((c, i) => (
              <CandidateRow key={c.shipment_id} candidate={c} top={i === 0}
                onConfirm={() => onConfirm(c.shipment_id)} confirming={confirming} />
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 pt-3 border-t border-zinc-900">
        <p className="text-[10px] text-zinc-500 italic">
          No good match? Bootstrap a new shipment from the logger data.
        </p>
        <Button variant="outline" size="sm" onClick={onCreate}
          className="border-zinc-700 hover:bg-zinc-800 text-xs"
          data-testid="logger-create-shipment-btn">
          <Plus size={11} className="mr-1" /> Create new shipment from logger
        </Button>
      </div>
    </div>
  );
}

/**
 * RoutePreviewMap — lightweight Leaflet preview of the parsed logger trace.
 * Start / end markers + polyline. Helps operators validate the upload before
 * committing to attach / create.
 */
function RoutePreviewMap({ points, first, last }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current || !first || !last) return;
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }
    const map = L.map(mapRef.current, { zoomControl: true, attributionControl: false })
      .setView([first.lat, first.lng], 6);
    mapInstanceRef.current = map;
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19, subdomains: "abcd",
    }).addTo(map);

    const coords = points
      .filter((p) => p.lat != null && p.lng != null)
      .map((p) => [p.lat, p.lng]);

    if (coords.length > 1) {
      const line = L.polyline(coords, { color: "#2dd4bf", weight: 3, opacity: 0.9 }).addTo(map);
      map.fitBounds(line.getBounds(), { padding: [20, 20] });
    } else {
      map.setView([first.lat, first.lng], 6);
    }

    // Start marker (teal)
    L.circleMarker([first.lat, first.lng], {
      radius: 8, color: "#2dd4bf", weight: 2, fillColor: "#2dd4bf", fillOpacity: 0.9,
    }).bindTooltip("Start", { permanent: false }).addTo(map);

    // End marker (amber)
    L.circleMarker([last.lat, last.lng], {
      radius: 8, color: "#fbbf24", weight: 2, fillColor: "#fbbf24", fillOpacity: 0.9,
    }).bindTooltip("End", { permanent: false }).addTo(map);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [points, first, last]);

  if (!first || !last) {
    return (
      <Card className="bg-zinc-900 border-zinc-800 p-4 text-center text-xs text-zinc-500"
        data-testid="logger-route-preview-empty">
        <MapTrifold size={20} weight="duotone" className="mx-auto text-zinc-700 mb-1" />
        No GPS data in this file — route preview unavailable.
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900 border-zinc-800 overflow-hidden" data-testid="logger-route-preview">
      <div className="px-3 py-2 border-b border-zinc-800 flex items-center gap-2">
        <MapTrifold size={12} className="text-teal-400" />
        <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-mono">Route Preview</span>
        <span className="text-[10px] text-zinc-600 ml-auto font-mono">
          start {first.lat.toFixed(3)},{first.lng.toFixed(3)} → end {last.lat.toFixed(3)},{last.lng.toFixed(3)}
        </span>
      </div>
      <div ref={mapRef} style={{ height: 220, width: "100%" }} />
    </Card>
  );
}

function CandidateRow({ candidate: c, top, onConfirm, confirming }) {
  const pct = Math.min(100, c.score);
  const color = c.score >= 75 ? "text-emerald-400" : c.score >= 50 ? "text-teal-400" : c.score >= 25 ? "text-amber-400" : "text-zinc-500";
  return (
    <div className={`p-3 rounded-sm border ${top ? "border-teal-500/30 bg-teal-500/5" : "border-zinc-800 bg-zinc-900/50"} flex items-center gap-4`}
      data-testid={`logger-candidate-${c.shipment_id}`}>
      <div className={`text-xl font-mono font-bold ${color} w-12 text-center`}>{c.score}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-zinc-100 truncate">{c.shipment_name}</div>
        <div className="text-[10px] text-zinc-500 font-mono">
          {c.customer || "—"} · {c.status || "—"}
        </div>
        <div className="mt-1.5 grid grid-cols-4 gap-1.5 text-[9.5px] text-zinc-500 font-mono">
          <Signal label="Origin"  data={c.signals.origin}      />
          <Signal label="Dest"    data={c.signals.destination} />
          <Signal label="Time"    data={c.signals.time}        />
          <Signal label="Path"    data={c.signals.path}        />
        </div>
      </div>
      <div className="w-32">
        <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
          <div className={`h-full ${c.score >= 75 ? "bg-emerald-400" : c.score >= 50 ? "bg-teal-400" : c.score >= 25 ? "bg-amber-400" : "bg-zinc-600"} transition-all`}
            style={{ width: `${pct}%` }} />
        </div>
      </div>
      <Button size="sm" onClick={onConfirm} disabled={confirming}
        className={`text-xs ${top ? "bg-teal-500 hover:bg-teal-400 text-zinc-950" : "bg-zinc-800 hover:bg-zinc-700 text-zinc-200"}`}
        data-testid={`logger-confirm-${c.shipment_id}`}>
        Attach <ArrowRight size={11} className="ml-1" />
      </Button>
    </div>
  );
}

function Signal({ label, data }) {
  if (!data) return <div className="text-zinc-700">—</div>;
  const pts = data.points || 0;
  return (
    <div title={JSON.stringify(data)} className="truncate">
      <span className="text-zinc-600">{label}</span>{" "}
      <span className={pts > 0 ? "text-teal-300" : "text-zinc-600"}>+{Math.round(pts)}</span>
    </div>
  );
}

function StatCard({ icon, label, value, sub }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
        {icon} {label}
      </div>
      <div className="text-base font-mono text-zinc-100">{value ?? "—"}</div>
      {sub && <div className="text-[10px] text-zinc-500 font-mono mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

function CreateStep({ form, setForm, importDoc, onBack, onSubmit, submitting }) {
  const s = importDoc.stats || {};
  // Reverse-geocode preview — mirrors the MatchStep stats card so the operator
  // confirms WHERE this shipment will start and end before saving it.
  const [geo, setGeo] = useState(null);
  useEffect(() => {
    let active = true;
    if (!importDoc?.id) return;
    axios.get(`${API}/logger-imports/${importDoc.id}/geocode`)
      .then((r) => { if (active) setGeo(r.data); })
      .catch(() => {});
    return () => { active = false; };
  }, [importDoc?.id]);
  const oName = geo?.origin?.name || geo?.origin?.city;
  const dName = geo?.destination?.name || geo?.destination?.city;
  return (
    <div className="space-y-3">
      <Field label="Shipment Name *">
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g. RTE-001 · Memphis → Atlanta · 2026-05-30"
          autoFocus
          className="bg-zinc-900 border-zinc-800 text-zinc-200 h-9"
          data-testid="logger-new-name" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Customer">
          <Input value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })}
            className="bg-zinc-900 border-zinc-800 text-zinc-200 h-9"
            data-testid="logger-new-customer" />
        </Field>
        <Field label="Cargo Type">
          <Input value={form.cargo_type} onChange={(e) => setForm({ ...form, cargo_type: e.target.value })}
            className="bg-zinc-900 border-zinc-800 text-zinc-200 h-9"
            data-testid="logger-new-cargo" />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Temp Min (°F)">
          <Input type="number" value={form.temp_min} onChange={(e) => setForm({ ...form, temp_min: e.target.value })}
            className="bg-zinc-900 border-zinc-800 text-zinc-200 h-9 font-mono"
            data-testid="logger-new-temp-min" />
        </Field>
        <Field label="Temp Max (°F)">
          <Input type="number" value={form.temp_max} onChange={(e) => setForm({ ...form, temp_max: e.target.value })}
            className="bg-zinc-900 border-zinc-800 text-zinc-200 h-9 font-mono"
            data-testid="logger-new-temp-max" />
        </Field>
      </div>
      <div className="text-[10px] text-zinc-500 italic bg-zinc-900/50 border border-zinc-800 rounded-sm p-3" data-testid="logger-create-route-preview">
        <div className="flex items-baseline justify-between gap-3 mb-1">
          <span className="text-[9px] uppercase tracking-wider text-zinc-600 not-italic">Origin / Destination (auto-pinned)</span>
        </div>
        {(oName || dName) ? (
          <div className="text-zinc-200 not-italic mb-1">
            <span className="font-medium">{oName || "—"}</span>
            <span className="text-zinc-600 mx-2">→</span>
            <span className="font-medium">{dName || "—"}</span>
          </div>
        ) : null}
        <span className="text-zinc-400">
          {s.first_point ? `${s.first_point.lat.toFixed(3)}, ${s.first_point.lng.toFixed(3)}` : "—"}
          <span className="text-zinc-600 mx-1">→</span>
          {s.last_point ? `${s.last_point.lat.toFixed(3)}, ${s.last_point.lng.toFixed(3)}` : "—"}
        </span>
      </div>
      <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-900">
        <Button variant="outline" size="sm" onClick={onBack} disabled={submitting}
          className="border-zinc-700 hover:bg-zinc-800 text-xs">Back</Button>
        <Button size="sm" onClick={onSubmit} disabled={submitting}
          className="bg-teal-500 hover:bg-teal-400 text-zinc-950 text-xs"
          data-testid="logger-create-submit">
          <CheckCircle size={11} weight="bold" className="mr-1" />
          {submitting ? "Creating…" : "Create Shipment & Replay"}
        </Button>
      </div>
    </div>
  );
}

function AttachedStep({ summary, stats, onDone, doneLabel = "Done" }) {
  return (
    <div className="space-y-4" data-testid="logger-attached-summary">
      <div className="text-center py-4">
        <CheckCircle size={42} weight="duotone" className="mx-auto text-emerald-400 mb-2" />
        <div className="text-zinc-100 font-semibold text-lg">Logger Attached</div>
        <div className="text-xs text-zinc-500 mt-1">
          Data replayed through the alert engine.
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <StatCard icon={<ChartLineUp size={14} className="text-emerald-400" />} label="Points Replayed" value={summary?.points_replayed ?? 0} />
        <StatCard icon={<Warning size={14} className="text-amber-400" />} label="Alerts Generated" value={summary?.alerts_generated ?? 0} />
        <StatCard icon={<FileText size={14} className="text-sky-400" />} label="Shipment" value={summary?.shipment_name?.slice(0, 18) || "—"} sub={summary?.device_name} />
      </div>
      <div className="text-[10px] text-zinc-500 italic">
        Route playback, facility arrival/departure events, temperature/humidity excursions, light/tamper events
        and any triggered corrective actions are now live on the shipment.
      </div>
      <div className="flex items-center justify-end pt-3 border-t border-zinc-900">
        <Button size="sm" onClick={onDone}
          className="bg-teal-500 hover:bg-teal-400 text-zinc-950 text-xs"
          data-testid="logger-attached-done">
          <CheckCircle size={11} weight="bold" className="mr-1" /> {doneLabel}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
